<template>
    <Head title="PM Plans" />

    <div class="py-6">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <!-- Header -->
            <div class="flex justify-between items-center mb-6">
                <div class="flex items-center gap-3">
                    <div>
                        <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">PM Plans</h1>
                        <p class="mt-1 text-sm text-gray-600 dark:text-secondary-400">Create and manage preventive maintenance plan templates</p>
                    </div>
                    <InfoButton title="PM Plans Management" variant="default">
                        <p>
                            <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                            PM Plans are reusable preventive maintenance templates that define recurring maintenance tasks,
                            frequencies, and checklists. Once created, these plans are linked to assets to automatically
                            generate PM schedules and work orders at specified intervals. PM Plans ensure consistent maintenance
                            procedures across similar assets and reduce setup time for preventive maintenance programs.
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                            • <strong>Quick Stats:</strong> View Total Plans, Active Plans, Plans with Schedules (linked to
                              assets), and Active Schedules (total across all assets)<br>
                            • <strong>Create PM Plan:</strong> Click "New PM Plan" to create a reusable template with frequency,
                              tasks, and work order templates<br>
                            • <strong>Search:</strong> Find plans by name or description using the search box<br>
                            • <strong>View Plan:</strong> Click "View" to see plan details, task checklists, and linked asset
                              schedules<br>
                            • <strong>Edit Plan:</strong> Modify plan details, frequencies, or tasks. Changes apply to future
                              work orders only<br>
                            • <strong>Link to Assets:</strong> After creating a plan, navigate to Assets → Select Asset →
                              PM Schedule tab → "Add PM Plan" to link the plan and create schedules<br>
                            • <strong>Frequency Column:</strong> Shows interval (e.g., "Every 1 months", "Every 3 weeks")<br>
                            • <strong>Priority:</strong> Determines urgency of generated work orders (Low, Normal, High, Urgent)<br>
                            • <strong>Schedules Column:</strong> Number of assets currently using this plan<br>
                            • <strong>Status:</strong> Active plans generate work orders; inactive plans are paused
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                            • <strong>Create generic plans</strong> - Design plans for asset types (e.g., "Monthly HVAC Check")
                              not specific assets<br>
                            • <strong>Reuse across assets</strong> - One plan can be linked to multiple similar assets,
                              saving setup time<br>
                            • <strong>Task checklists</strong> ensure consistent maintenance procedures across all technicians<br>
                            • <strong>Use placeholders</strong> in work order templates - {plan_name} and {asset_name} auto-fill
                              when work orders generate<br>
                            • <strong>Deactivate instead of delete</strong> - Keeps historical data while stopping new work
                              order generation<br>
                            • <strong>Plans with Schedules stat</strong> shows which plans are actively deployed<br>
                            • <strong>Edit carefully</strong> - Changes to task lists affect future work orders but not existing
                              ones<br>
                            • <strong>Duplicate plans</strong> from the detail page to create variations quickly
                        </p>
                    </InfoButton>
                </div>
                <Link
                    v-if="$page.props.auth.permissions.includes('maintenance.pm_schedules.create')"
                    :href="route('settings.maintenance.pm-plans.create')"
                    class="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                    </svg>
                    New PM Plan
                </Link>
            </div>

            <!-- Statistics Cards -->
            <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div class="bg-white dark:bg-secondary-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-indigo-100 dark:bg-indigo-900/30 rounded-md p-3">
                            <svg class="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600 dark:text-secondary-400">Total Plans</p>
                            <p class="text-2xl font-semibold text-gray-900 dark:text-white">{{ statistics.total_plans || 0 }}</p>
                        </div>
                    </div>
                </div>

                <div class="bg-white dark:bg-secondary-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-green-100 dark:bg-green-900/30 rounded-md p-3">
                            <svg class="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600 dark:text-secondary-400">Active Plans</p>
                            <p class="text-2xl font-semibold text-green-600 dark:text-green-400">{{ statistics.active_plans || 0 }}</p>
                        </div>
                    </div>
                </div>

                <div class="bg-white dark:bg-secondary-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 rounded-md p-3">
                            <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600 dark:text-secondary-400">With Schedules</p>
                            <p class="text-2xl font-semibold text-blue-600 dark:text-blue-400">{{ statistics.plans_with_schedules || 0 }}</p>
                        </div>
                    </div>
                </div>

                <div class="bg-white dark:bg-secondary-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-purple-100 dark:bg-purple-900/30 rounded-md p-3">
                            <svg class="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600 dark:text-secondary-400">Active Schedules</p>
                            <p class="text-2xl font-semibold text-purple-600 dark:text-purple-400">{{ statistics.total_active_schedules || 0 }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Search -->
            <div class="bg-white dark:bg-secondary-800 rounded-lg shadow mb-6">
                <div class="p-4">
                    <div class="flex-1 max-w-lg">
                        <div class="relative">
                            <input
                                v-model="searchQuery"
                                type="text"
                                placeholder="Search by plan name or description..."
                                class="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                @input="debounceSearch"
                            />
                            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg class="h-5 w-5 text-gray-400 dark:text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- PM Plans Table -->
            <div class="bg-white dark:bg-secondary-800 rounded-lg shadow overflow-hidden mb-6">
                <table class="min-w-full divide-y divide-gray-200 dark:divide-secondary-700">
                    <thead class="bg-gray-50 dark:bg-secondary-750">
                        <tr>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                                Plan Name
                            </th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                                Frequency
                            </th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                                Priority
                            </th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                                Schedules
                            </th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                                Status
                            </th>
                            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-white dark:bg-secondary-800 divide-y divide-gray-200 dark:divide-secondary-700">
                        <tr v-for="plan in pmPlans.data" :key="plan.id" class="hover:bg-gray-50 dark:hover:bg-secondary-750">
                            <td class="px-6 py-4">
                                <div>
                                    <div class="text-sm font-medium text-gray-900 dark:text-white">{{ plan.name }}</div>
                                    <div v-if="plan.description" class="text-sm text-gray-500 dark:text-secondary-400 truncate max-w-md">
                                        {{ plan.description }}
                                    </div>
                                </div>
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                Every {{ plan.frequency_value }} {{ plan.frequency_unit }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span
                                    :class="[
                                        'px-2 py-1 text-xs font-medium rounded-full',
                                        getPriorityClass(plan.priority)
                                    ]"
                                >
                                    {{ plan.priority_label }}
                                </span>
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                <div class="flex items-center">
                                    <svg class="w-4 h-4 mr-1 text-gray-400 dark:text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    {{ plan.asset_schedules_count || 0 }}
                                </div>
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span
                                    :class="[
                                        'px-2 py-1 text-xs font-medium rounded-full',
                                        plan.is_active ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' : 'bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-secondary-300'
                                    ]"
                                >
                                    {{ plan.is_active ? 'Active' : 'Inactive' }}
                                </span>
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <div class="flex items-center justify-end space-x-2">
                                    <Link
                                        :href="route('settings.maintenance.pm-plans.show', plan.id)"
                                        class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                                    >
                                        View
                                    </Link>
                                    <Link
                                        v-if="$page.props.auth.permissions.includes('maintenance.pm_schedules.manage')"
                                        :href="route('settings.maintenance.pm-plans.edit', plan.id)"
                                        class="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                                    >
                                        Edit
                                    </Link>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <div v-if="pmPlans.data.length > 0" class="bg-white dark:bg-secondary-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-secondary-700 sm:px-6 rounded-lg shadow">
                <div class="flex-1 flex justify-between sm:hidden">
                    <Link
                        v-if="pmPlans.prev_page_url"
                        :href="pmPlans.prev_page_url"
                        class="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-secondary-600 text-sm font-medium rounded-md text-gray-700 dark:text-secondary-300 bg-white dark:bg-secondary-700 hover:bg-gray-50 dark:hover:bg-secondary-600"
                    >
                        Previous
                    </Link>
                    <Link
                        v-if="pmPlans.next_page_url"
                        :href="pmPlans.next_page_url"
                        class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-secondary-600 text-sm font-medium rounded-md text-gray-700 dark:text-secondary-300 bg-white dark:bg-secondary-700 hover:bg-gray-50 dark:hover:bg-secondary-600"
                    >
                        Next
                    </Link>
                </div>
                <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                        <p class="text-sm text-gray-700 dark:text-secondary-300">
                            Showing
                            <span class="font-medium">{{ pmPlans.from }}</span>
                            to
                            <span class="font-medium">{{ pmPlans.to }}</span>
                            of
                            <span class="font-medium">{{ pmPlans.total }}</span>
                            results
                        </p>
                    </div>
                    <div>
                        <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                            <template v-for="link in pmPlans.links" :key="link.label">
                                <Link
                                    v-if="link.url"
                                    :href="link.url"
                                    :class="[
                                        'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                                        link.active
                                            ? 'z-10 bg-blue-50 dark:bg-blue-900/30 border-blue-500 dark:border-blue-600 text-blue-600 dark:text-blue-400'
                                            : 'bg-white dark:bg-secondary-700 border-gray-300 dark:border-secondary-600 text-gray-500 dark:text-secondary-300 hover:bg-gray-50 dark:hover:bg-secondary-600'
                                    ]"
                                    v-html="link.label"
                                />
                                <span
                                    v-else
                                    :class="[
                                        'relative inline-flex items-center px-4 py-2 border text-sm font-medium cursor-not-allowed opacity-50',
                                        'bg-white dark:bg-secondary-700 border-gray-300 dark:border-secondary-600 text-gray-400 dark:text-secondary-500'
                                    ]"
                                    v-html="link.label"
                                />
                            </template>
                        </nav>
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div v-if="pmPlans.data.length === 0" class="bg-white dark:bg-secondary-800 rounded-lg shadow p-12 text-center">
                <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No PM plans found</h3>
                <p class="mt-1 text-sm text-gray-500 dark:text-secondary-400">Get started by creating a new PM plan template.</p>
                <div v-if="$page.props.auth.permissions.includes('maintenance.pm_schedules.create')" class="mt-6">
                    <Link
                        :href="route('settings.maintenance.pm-plans.create')"
                        class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                        </svg>
                        New PM Plan
                    </Link>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    pmPlans: Object,
    statistics: Object,
    filters: Object,
});

const searchQuery = ref(props.filters.search || '');

let searchTimeout = null;

const debounceSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        applySearch();
    }, 500);
};

const applySearch = () => {
    router.get(route('settings.maintenance.pm-plans.index'), {
        search: searchQuery.value,
    }, {
        preserveState: true,
        preserveScroll: true,
    });
};

const getPriorityClass = (priority) => {
    const classes = {
        low: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400',
        normal: 'bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-secondary-300',
        high: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-400',
        urgent: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400',
    };
    return classes[priority] || 'bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-secondary-300';
};
</script>
